import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Check, ChevronDown } from 'lucide-react';
const tomos = [{
  num: 'T1',
  title: 'Cómo Crear un Presupuesto que Genera Confianza',
  tagline: 'La base estructural que te diferencia de la competencia informal.',
  desc: 'Comprendé la estructura perfecta detrás de una cotización irresistible. Aprendé a desglosar por ambientes, presentar ítems claros y generar un documento PDF profesional que justifique cada centavo de tu valor sin dejar margen a dudas.',
  bullets: ['Desglose profesional por ambientes y etapas.', 'Presentación visual de alto impacto.', 'Cálculo de imprevistos transparente.']
}, {
  num: 'T2',
  title: 'El Flujo de Trabajo – De la Visita al Cierre',
  tagline: 'La metodología exacta desde el primer contacto hasta la firma.',
  desc: 'Dominá el timing perfecto de la negociación. Implementá checklists específicos para la visita de diagnóstico, reducí los tiempos muertos y ejecutá las transiciones críticas para que el cliente tome la decisión de contratarte antes de que te vayas del lugar.'
}, {
  num: 'T3',
  title: 'Presentar el Presupuesto – El Arte de Explicar el Valor',
  tagline: 'Estrategias de comunicación para neutralizar el "está caro".',
  desc: 'Dejá de vender metros cuadrados de ladrillo o litros de pintura; aprendé a vender la transformación del espacio. Implementá la estrategia de cotización en 3 niveles para dar opciones al cliente y guiarlo psicológicamente hacia tu oferta ideal.'
}, {
  num: 'T4',
  title: 'El Seguimiento que No Molesta – Estrategia de Reactivación',
  tagline: 'Cómo recuperar presupuestos enfriados sin perder la postura.',
  desc: 'El dinero está en el seguimiento. Aprendé el timing exacto y los mensajes de valor específicos para reabrir conversaciones con clientes que dejaron de responder, reactivando tu flujo de caja de manera profesional y orgánica.'
}, {
  num: 'T5',
  title: 'Acabados Finales y Pintura – El Maestro de las Reformas',
  tagline: 'Cotización milimétrica de las terminaciones más visuales.',
  desc: 'Evitá los errores más comunes al presupuestar revestimientos, pintura y detalles finos. Aprendé a calcular materiales y mano de obra in situ, blindándote contra los reclamos típicos de los clientes exigentes sobre la calidad del acabado.'
}, {
  num: 'T6',
  title: 'El Escudo Comercial y Plan de Acción',
  tagline: 'Las condiciones generales de trabajo inamovibles.',
  desc: 'Protegé tu salud financiera y mental. Establecé las reglas del juego antes de mover una sola herramienta: políticas de pago frente a inflación, gestión de cambios de diseño a mitad de camino y el cierre legal para congelar precios sin perder rentabilidad.'
}];
export function TomosContent() {
  const [openIndex, setOpenIndex] = useState<number>(0);
  return <section className="py-10 sm:py-16 lg:py-24 bg-emerald-50" data-id="element-254">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-255">
        <div className="text-center mb-8 sm:mb-16" data-id="element-256">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold mb-3 sm:mb-4 text-[#2D5BAA]" data-id="element-257">
            CONTENIDO COMPLETO: LOS 6 TOMOS
          </h2>
          <p className="text-base sm:text-xl text-gray-600 font-medium" data-id="element-258">
            Tu hoja de ruta para dominar la gestión comercial de tus reformas
          </p>
        </div>

        <div className="space-y-3 sm:space-y-6" data-id="element-259">
          {tomos.map((tomo, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className={`bg-brand-bg rounded-xl sm:rounded-2xl border ${isOpen ? 'border-brand-accent shadow-md' : 'border-gray-200 shadow-sm'} overflow-hidden transition-all`} data-id="element-260">
                <button onClick={() => setOpenIndex(isOpen ? -1 : index)} className="w-full text-left p-4 sm:p-6 md:p-8 flex items-center justify-between focus:outline-none" data-id="element-261">
                  <div className="flex items-center pr-4" data-id="element-262">
                    <span className={`font-black text-sm sm:text-xl px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg mr-3 sm:mr-4 transition-colors ${isOpen ? 'bg-brand-accent text-brand-dark' : 'bg-brand-dark text-white'}`} data-id="element-263">
                      {tomo.num}
                    </span>
                    <div data-id="element-264">
                      <h3 className="text-base sm:text-xl md:text-2xl font-bold text-brand-dark leading-tight" data-id="element-265">
                        {tomo.title}
                      </h3>
                      {/* Show tagline on mobile only when closed to save space, or always on desktop */}
                      <p className={`text-brand-accent font-bold text-xs sm:text-sm mt-1 ${isOpen ? 'block' : 'hidden sm:block'}`} data-id="element-266">
                        {tomo.tagline}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`w-5 h-5 sm:w-6 sm:h-6 text-gray-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-brand-accent' : ''}`} data-id="element-267" />
                </button>

                <AnimatePresence data-id="element-268">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-269">
                      <div className="px-4 pb-5 sm:px-6 sm:pb-8 md:px-8 md:pb-8 pt-0 relative" data-id="element-270">
                        <div className="absolute -right-10 -bottom-10 text-brand-blue/5 pointer-events-none hidden sm:block" data-id="element-271">
                          <BookOpen className="w-48 h-48" data-id="element-272" />
                        </div>

                        <p className="text-brand-accent font-bold text-sm sm:hidden mb-3" data-id="element-273">
                          {tomo.tagline}
                        </p>

                        <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6 relative z-10" data-id="element-274">
                          {tomo.desc}
                        </p>

                        {tomo.bullets && <ul className="space-y-2 sm:space-y-3 mt-4 sm:mt-6 bg-white p-4 sm:p-6 rounded-xl border border-gray-100 relative z-10" data-id="element-275">
                            {tomo.bullets.map((bullet, i) => <li key={i} className="flex items-start" data-id="element-276">
                                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" data-id="element-277" />
                                <span className="text-gray-800 text-sm sm:text-base font-medium" data-id="element-278">
                                  {bullet}
                                </span>
                              </li>)}
                          </ul>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}